import { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { CheckCircle2, ChevronDown, ChevronUp, Inbox, XCircleIcon } from "lucide-react";
import Spinner from "../../../../../../components/shared/Spinner";
import Avatar from "../../../../../../components/shared/Avatar";
import { usePermissions } from "../../../hooks/usePermissions";
import { useUser } from "../../../../../../utils/hooks/user";
import { getOrderedWeekdays } from "../../../helpers/getOrderedWeekdays";
import { weekdays } from "../../../../../../components/shared/inputs/WeekdaySelect/types";
import { renderUserName } from "../../../../../../utils/helpers/user";
import { cn } from "../../../../../../lib/utils";

function SwapShifts({ approve, deny, loading, totalItems, data, refetch }: any) {
    const { t } = useTranslation();
    const user = useUser();
    const [open, setOpen] = useState<Record<string, boolean>>({});
    const { canApproveRequest, canDenyRequest } = usePermissions();
    const orderedWeekdays = getOrderedWeekdays();

    const getOrderedIndex = (date?: string) => {
        if (!date) return -1;
        const weekday = weekdays[moment(date).day()];
        return orderedWeekdays.indexOf(weekday);
    };

    return loading ? (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-7">
            <Spinner className="tw-text-primary" />
        </div>
    ) : totalItems === 0 ? (
        <div className="tw-flex tw-flex-col tw-items-center tw-text-neutral-500 tw-border tw-rounded-sm tw-py-7">
            <Inbox />
            <span>{t("no-data")}</span>
        </div>
    ) : (
        <div className="tw-space-y-4">
            {data?.map((item: any) => {
                const isOpen = open[item.id];
                const isApprover = (item?.requiredToBeApprovedBy || []).includes(user?.id);
                const isPending = item?.status === "pending" || item?.status === "pending-canceled";
                const originalShift = item.data?.schedule;
                const swapShift = item.data?.swapSchedule;

                return (
                    <div key={item.id} className="tw-border tw-border-neutral-200 tw-rounded-xl tw-bg-white tw-overflow-hidden">
                        <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
                            <div className="tw-flex tw-items-center tw-text-sm tw-gap-2">
                                <Avatar user={item.data.originalUser} size="sm" />
                                <span>{renderUserName(item.data.originalUser)}</span>
                            </div>

                            <div className="tw-flex tw-gap-3 tw-items-center">
                                {((canDenyRequest || isApprover) && isPending) && (
                                    <XCircleIcon
                                        className="tw-text-rose-500 tw-cursor-pointer"
                                        onClick={() => deny({ ...item, close: refetch })}
                                    />
                                )}
                                {((canApproveRequest || isApprover) && isPending) && (
                                    <CheckCircle2
                                        className="tw-text-primary tw-cursor-pointer"
                                        onClick={() => approve({ ...item, close: refetch })}
                                    />
                                )}

                                {isOpen ? (
                                    <ChevronUp
                                        className="tw-text-neutral-500 tw-cursor-pointer"
                                        size={19}
                                        onClick={() => setOpen((prev: any) => ({
                                            ...prev,
                                            [item.id]: false
                                        }))}
                                    />
                                ) : (
                                    <ChevronDown
                                        className="tw-text-neutral-500 tw-cursor-pointer"
                                        size={19}
                                        onClick={() => setOpen((prev: any) => ({
                                            ...prev,
                                            [item.id]: true
                                        }))}
                                    />
                                )}
                            </div>
                        </div>

                        {isOpen && (
                            <div className="tw-grid tw-grid-cols-7 tw-border-t tw-border-neutral-200 tw-divide-x tw-divide-neutral-200 tw-bg-neutral-50/30">
                                {orderedWeekdays.map((day, idx) => {
                                    const isOriginalDay = idx === getOrderedIndex(originalShift?.date);
                                    const isSwapDay = idx === getOrderedIndex(swapShift?.date);
                                    const originalLevel = originalShift?.sections?.[0]?.level1 ||
                                        originalShift?.sections?.[0]?.level2 ||
                                        originalShift?.sections?.[0]?.level3 ||
                                        originalShift?.sections?.[0]?.level4;
                                    const swapLevel = swapShift?.sections?.[0]?.level1 ||
                                        originalShift?.sections?.[0]?.level2 ||
                                        originalShift?.sections?.[0]?.level3 ||
                                        originalShift?.sections?.[0]?.level4;

                                    return (
                                        <div key={idx} className="tw-p-2 tw-min-w-0">
                                            <div className="tw-flex tw-justify-between tw-items-center tw-gap-1 tw-text-xs tw-mb-3 tw-px-1">
                                                <span className={cn("tw-font-medium tw-text-neutral-500 tw-truncate", (isOriginalDay || isSwapDay) && "tw-text-primary tw-font-black")}>
                                                    {t(day)}
                                                </span>
                                                <span className="tw-text-primary tw-font-black tw-whitespace-nowrap tw-shrink-0">
                                                    {isOriginalDay && moment(originalShift?.date).format("M/D")}
                                                    {isSwapDay && moment(swapShift?.startDate).format("M/D")}
                                                </span>
                                            </div>

                                            {isOriginalDay && (
                                                <div className="tw-bg-rose-50 tw-border tw-border-rose-100 tw-rounded-md tw-p-2 tw-mb-2">
                                                    <div className="tw-text-xs tw-font-bold tw-text-neutral-800">
                                                        {moment.parseZone(originalShift.startDate).format("ha")} - {moment.parseZone(originalShift.endDate).format("ha")}
                                                    </div>
                                                    <div className="tw-text-xs tw-text-neutral-500 tw-truncate tw-uppercase tw-mt-1">
                                                        {originalShift?.sections?.[0]?.payCode?.code} •
                                                        {originalLevel?.glSegment || originalLevel?.code}
                                                    </div>
                                                </div>
                                            )}

                                            {isSwapDay && (
                                                <div className="tw-bg-white tw-border-2 tw-border-dashed tw-border-amber-300 tw-rounded-md tw-p-2 tw-relative">
                                                    <div className="tw-text-xs tw-font-bold tw-text-neutral-800 tw-line-through tw-decoration-neutral-400">
                                                        {moment.parseZone(swapShift.startDate).format("ha")} - {moment.parseZone(swapShift.endDate).format("ha")}
                                                    </div>
                                                    <div className="tw-text-xs tw-text-neutral-500 tw-truncate tw-mt-1">
                                                        {swapShift?.sections?.[0]?.payCode?.code} • {swapLevel?.glSegment || swapLevel?.code}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default SwapShifts;